import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {authService} from '@/services/authService';
import type {LoginData, LoginResponse, User} from '@/types/auth';
import {clearAuthStorage, savePermissions} from '@/utils/auth';

export const login = createAsyncThunk<LoginResponse, LoginData>(
  'auth/login',
  async (data) => {
    const response = await authService.login(data);
    localStorage.setItem('token', response.token);
    if (response.roles) {
      localStorage.setItem('roles', JSON.stringify(response.roles));
    } else {
      localStorage.removeItem('roles');
    }
    savePermissions(response.permissions);
    return response;
  }
);

export const getCurrentUser = createAsyncThunk<User>(
  'auth/getCurrentUser',
  async () => {
    return await authService.getCurrentUser();
  }
);

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      clearAuthStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          id: action.payload.userId,
          username: action.payload.username,
          realName: action.payload.realName,
          roles: action.payload.roles,
        };
        localStorage.setItem('username', action.payload.username);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '登录失败';
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取用户信息失败';
      });
  },
});

export const { clearError, logout } = authSlice.actions;
export default authSlice.reducer;
